import { useEffect, useMemo, useRef, useState } from 'react';
import {
  deriveStrings,
  useTuningStore,
  useUiStore,
} from '@store/index';
import { useAudioEngine, useRafLoop } from '@hooks/index';
import { computeStringYs } from '@engine/scale';
import {
  startTuner,
  freqToNote,
  type TunerPitch,
  type TunerHandle,
} from '@engine/audio';
import './TunerView.css';

/** Cents window counted as "in tune" — the trail tightens into a
 *  brighter, more-saturated glow inside. */
const IN_TUNE_CENTS = 5;

/** How many seconds of pitch history the trail shows before fading. */
const TRAIL_SECONDS = 4;

/** Trail colour — saturated green that glows neon-style. Chosen to
 *  pair cleanly with the yellow root + red fifth string colours
 *  (distinct hue family) and to read instantly as "this is your
 *  detected pitch" without needing a legend. */
const TRAIL_RGB = '74, 222, 128';          // green-400
const TRAIL_RGB_INTUNE = '134, 239, 172';  // green-300 — brighter inside ±5¢

interface TrailPoint {
  t: number;        // performance.now() timestamp
  centsOff: number; // signed cents deviation from this trail's host string
  inTune: boolean;
  rms: number;      // signal RMS at the detection frame, in [0, 1]
  /** False when this point should NOT be visually connected to the
   *  previous one — either too much wall-clock time has elapsed since
   *  the last detection (silence between plucks) or the pitch jumped
   *  too far (new attack, not a continuous slide). Without this, a
   *  long silence followed by a fresh pluck on the same string draws
   *  a straight bridge across the whole silence — a "fake" line that
   *  doesn't reflect anything the player actually did. */
  continuous: boolean;
}

/**
 * A sample on the single, CONTINUOUS main pitch line.
 *
 * Unlike TrailPoint (which is routed onto one string's row and stores
 * a cents offset from that string), a PitchPoint keeps the ABSOLUTE
 * detected pitch. The main line is drawn straight from absolute pitch
 * → Y, so it flows freely between string rows — no per-string routing,
 * so no flicker / doubling when the pitch sits near a string boundary.
 */
interface PitchPoint {
  t: number;
  midiFloat: number;
  rms: number;
  inTune: boolean;
  continuous: boolean;
}

/** A main-line sample resolved to canvas coordinates, ready to stroke. */
interface DrawPoint {
  x: number;
  y: number;
  alpha: number;
  inTune: boolean;
  continuous: boolean;
}

/** Max time gap between consecutive samples before the line breaks
 *  into a fresh sub-stroke. Generous (350 ms) so brief YIN dropouts
 *  mid-note don't chop a sustained note into pieces — the line simply
 *  bridges the short gap. Only a real stop (damped silence) exceeds
 *  it. The earlier 120 ms was the main cause of the "still segmented"
 *  look: every momentary dropout broke the line. */
const GAP_BREAK_MS = 350;
/** Max pitch jump between consecutive samples before the line breaks.
 *  Half a semitone is wider than any vibrato or rung-finger bend,
 *  narrower than any deliberate note change. */
const JUMP_BREAK_CENTS = 50;
/** A frame-to-frame leap bigger than this (semitones) is treated as a
 *  transient YIN mis-detection (octave / harmonic error) and DROPPED
 *  rather than recorded — so it never punches a hole in the line. See
 *  the capture effect. Matches tunerPitch's octave-error threshold. */
const JUMP_REJECT_SEMIS = 6;

/** How wide a per-string row is on the canvas, as a fraction of the
 *  gap to the nearest neighbouring string. Leaves a small margin so
 *  adjacent rows don't visually bleed into each other. */
const ROW_FILL = 0.8;
/** Cents deviation mapped to the edge of a row. Readings off by more
 *  than this get clipped, so a quiet misfire stays pinned at the edge
 *  instead of shooting off the top. ±80¢ is tight enough for a tuning
 *  display but leaves room to see pitch drift during a bend. */
const MAX_VIEW_CENTS = 80;

/** Sub-precision reference. Drawn as a dimmer band on the active
 *  string — "you're close, but not in tune yet". Half a semitone-ish
 *  is the boundary where a player notices the note "sounding right"
 *  but a tuner still flags it. */
const CLOSE_CENTS = 20;

/** Fraction of the canvas width reserved for the precision strip —
 *  the active string's row replayed at 10× vertical magnification with
 *  LINEAR cent spacing. Strip sits in the right ~third of the canvas,
 *  with a small right-edge margin so the StringsOverlay's x1 strings
 *  stay visible at the very right (no scale-jump confusion since they
 *  don't carry a trail there). Left ~two-thirds remains the x1
 *  time-history trail. */
const STRIP_WIDTH_FRAC = 1 / 3;
/** Vertical magnification factor inside the strip vs. the main row. */
const STRIP_MAGNIFICATION = 10;

/** dB mapping window for trail opacity. -50 dBFS sits just above the
 *  YIN gate (-60 dBFS, see yin.ts) so the dying tail of a pluck gets
 *  a long, soft fade across the full detected range instead of cliff-
 *  dropping to invisible at -40 dBFS. -15 dBFS is loud-pluck saturation
 *  — beyond it the trail is just "as bright as it gets". Linear in dB
 *  so equal-loudness drops map to equal opacity steps. */
const DB_FLOOR = -50;
const DB_CEIL = -15;
function rmsToAlpha(rms: number): number {
  if (rms <= 0) return 0;
  const db = 20 * Math.log10(rms);
  const t = (db - DB_FLOOR) / (DB_CEIL - DB_FLOOR);
  return Math.max(0, Math.min(1, t));
}

/**
 * One Euro Filter — an adaptive low-pass for a noisy 1-D signal.
 *
 * A FIXED smoother forces a trade-off: smooth enough to kill YIN's
 * per-frame jitter means laggy on a fast bend; low-lag means jittery.
 * The One Euro Filter dodges that — it lows the cut-off frequency
 * when the signal is slow (a held note → heavy smoothing, rock-steady
 * line) and RAISES it when the signal moves fast (a bend or attack →
 * little smoothing, near-zero lag). So the trail is both smooth AND
 * responsive, with no single fixed setting to compromise.
 *
 * `minCutoff` / `beta` / `dCutoff` are the only tunables — see the
 * defaults at the call site. Reference: https://gery.casiez.net/1euro/
 */
class OneEuroFilter {
  private xPrev = 0;
  private dxPrev = 0;
  private tPrev = 0;
  private started = false;
  /** Cut-off (Hz) used while the signal is still — lower = smoother
   *  hold, but a touch more lag entering a move. */
  private readonly minCutoff: number;
  /** How sharply the cut-off opens with signal speed — higher =
   *  snappier tracking of bends / attacks. */
  private readonly beta: number;
  /** Cut-off (Hz) for the speed estimate's own low-pass. */
  private readonly dCutoff: number;

  // Explicit field assignment (not constructor parameter properties)
  // so the class stays within `erasableSyntaxOnly`.
  constructor(minCutoff: number, beta: number, dCutoff: number) {
    this.minCutoff = minCutoff;
    this.beta = beta;
    this.dCutoff = dCutoff;
  }

  /** Drop all state so the next sample restarts the filter — call on
   *  silence so a fresh pluck doesn't lag out of the previous note. */
  reset(): void {
    this.started = false;
  }

  /** First-order low-pass smoothing factor for a given cut-off + dt. */
  private static alpha(cutoff: number, dt: number): number {
    const tau = 1 / (2 * Math.PI * cutoff);
    return 1 / (1 + tau / dt);
  }

  /** @param x   raw sample
   *  @param tMs timestamp in ms (performance.now scale) */
  filter(x: number, tMs: number): number {
    if (!this.started) {
      this.started = true;
      this.xPrev = x;
      this.dxPrev = 0;
      this.tPrev = tMs;
      return x;
    }
    const dt = Math.max(1e-3, (tMs - this.tPrev) / 1000); // seconds
    this.tPrev = tMs;
    // Smoothed speed estimate.
    const dx = (x - this.xPrev) / dt;
    const aD = OneEuroFilter.alpha(this.dCutoff, dt);
    const dxHat = aD * dx + (1 - aD) * this.dxPrev;
    this.dxPrev = dxHat;
    // Speed-dependent cut-off → fast motion barely smoothed.
    const cutoff = this.minCutoff + this.beta * Math.abs(dxHat);
    const aX = OneEuroFilter.alpha(cutoff, dt);
    const xHat = aX * x + (1 - aX) * this.xPrev;
    this.xPrev = xHat;
    return xHat;
  }
}

/**
 * Fullscreen tuner. Reuses the main stage's StringsOverlay for the
 * horizontal-strings look; renders a scrolling pitch trail canvas on
 * top so the player sees their detected pitch as a flowing line
 * (brushing across the strings), plus a readout chip + close button.
 *
 * While open:
 *   · Camera + hand detection suspended (CameraStage gates them).
 *   · String layout forced to cents spacing + low-pitch-on-top so the
 *     ruler reads like a real tuner. Prior layout restored on close.
 *   · Mic capture with iOS-friendly constraints (see tunerPitch.ts).
 */
export function TunerView({
  onClose,
  mode = 'tuner',
}: {
  onClose: () => void;
  /** 'tuner' — full tuning UI: forced cents + low-on-top layout, a
   *  green/red in-tune verdict, and the 10× precision strip. 'free' —
   *  Free Play: the tuner's pitch trail copied verbatim (identical
   *  cent mapping, smoothing and stroke) across the FULL canvas width
   *  over the player's CURRENT layout — but with NO strip and NO
   *  verdict. The line is exactly as smooth as the tuner's; the strip
   *  is the only thing dropped, so Free Play stays visually its own
   *  full-screen string view. */
  mode?: 'tuner' | 'free';
}) {
  const active = useTuningStore((s) => s.sets[s.activeIndex]!);
  const derived = useMemo(() => deriveStrings(active), [active]);

  const stringSpacing = useUiStore((s) => s.stringSpacing);
  const setStringSpacing = useUiStore((s) => s.setStringSpacing);
  const stringOrderLowTop = useUiStore((s) => s.stringOrderLowTop);
  const setStringOrderLowTop = useUiStore((s) => s.setStringOrderLowTop);

  const { engineRef } = useAudioEngine();

  // Adaptive smoother for the raw YIN pitch — see OneEuroFilter. Tuned
  // for đàn tranh: a low rest cut-off (steady note → rock-steady line)
  // with a strong speed term so a bend / pluck attack tracks snappily.
  // These three numbers are the only "feel" knobs for both the tuner
  // and Free Play trail responsiveness.
  const pitchFilterRef = useRef(new OneEuroFilter(2.5, 1.8, 1));

  const [pitch, setPitch] = useState<TunerPitch | null>(null);
  const [micError, setMicError] = useState<string | null>(null);
  const [micReady, setMicReady] = useState(false);

  // Stable pitch — the longest-held detection in the recent window.
  // The trail and indicator dots render from `pitch` directly so
  // vibrato + bend MOTION shows live; the readout chip below uses
  // `stablePitch` so the NOTE NAME stays parked on the central pitch
  // and persists during silence until a new dominant pitch arrives.
  const STABLE_WINDOW_MS = 1500;
  const samplesRef = useRef<TunerPitch[]>([]);
  const [stablePitch, setStablePitch] = useState<TunerPitch | null>(null);
  useEffect(() => {
    if (!pitch) return;
    const arr = samplesRef.current;
    arr.push(pitch);
    const cutoff = pitch.at - STABLE_WINDOW_MS;
    while (arr.length > 0 && arr[0]!.at < cutoff) arr.shift();
    // Mode of midiFloat at 10¢ resolution. Bucketed so vibrato wobble
    // around a centre note clusters into one bucket; bend-then-settle
    // moves the mode the moment the new pitch dominates the count.
    // Ties broken by most recent so a fresh sustained note overrides
    // an equal-count older note.
    const buckets = new Map<
      number,
      { count: number; sumMidi: number; sumRms: number; lastT: number; freqAtLast: number; confAtLast: number }
    >();
    for (const s of arr) {
      const key = Math.round(s.midiFloat * 10);
      const e =
        buckets.get(key) ?? {
          count: 0,
          sumMidi: 0,
          sumRms: 0,
          lastT: 0,
          freqAtLast: 0,
          confAtLast: 0,
        };
      e.count += 1;
      e.sumMidi += s.midiFloat;
      e.sumRms += s.rms;
      if (s.at > e.lastT) {
        e.lastT = s.at;
        e.freqAtLast = s.freq;
        e.confAtLast = s.confidence;
      }
      buckets.set(key, e);
    }
    let bestCount = 0;
    let bestLastT = 0;
    let bestEntry: typeof buckets extends Map<number, infer V> ? V | null : null = null;
    for (const e of buckets.values()) {
      if (
        e.count > bestCount ||
        (e.count === bestCount && e.lastT > bestLastT)
      ) {
        bestCount = e.count;
        bestLastT = e.lastT;
        bestEntry = e;
      }
    }
    if (bestEntry) {
      setStablePitch({
        freq: bestEntry.freqAtLast,
        confidence: bestEntry.confAtLast,
        midiFloat: bestEntry.sumMidi / bestEntry.count,
        at: bestEntry.lastT,
        rms: bestEntry.sumRms / bestEntry.count,
      });
    }
  }, [pitch]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Position-driven ref for the readout chip. The RAF loop only moves
  // the chip when a NEW sub-stroke starts on the active string (fresh
  // pluck or a string switch); during a sustained sound the chip stays
  // put, so it doesn't follow vibrato.
  const readoutRef = useRef<HTMLDivElement>(null);
  /** Anchor stroke that the chip is currently locked to. When the
   *  active string's most recent continuous=false point's (stringIdx,
   *  t) tuple changes, we recompute chip position. */
  const chipAnchorRef = useRef<{ stringIdx: number; t: number } | null>(null);
  const chipPosRef = useRef<{ leftCss: number; topCss: number } | null>(null);
  // The single, continuous main pitch line — absolute pitch over time.
  // One flat array (not a per-string map): the line is drawn straight
  // from absolute pitch → Y so it flows freely between string rows.
  const trailRef = useRef<PitchPoint[]>([]);
  // Consecutive samples dropped as transient jump outliers — capped so
  // a genuine (sustained) note change is eventually accepted.
  const jumpDropStreakRef = useRef(0);
  // Per-string trails, keyed by stringIndex, kept ONLY to feed the
  // tuner's precision strip (which by nature shows one string's row
  // magnified). The main pitch line no longer uses these.
  const trailsRef = useRef<Map<number, TrailPoint[]>>(new Map());
  // Last string the pitch was nearest to — used so the tuner strip
  // keeps showing (and fading) that string while the mic is quiet.
  const lastActiveStringRef = useRef<number>(-1);

  // Mute ringing voices on entry so the mic doesn't pick up speaker
  // output from the app's own synth.
  useEffect(() => {
    engineRef.current?.muteAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save layout on mount, force cents + low-on-top, restore on unmount.
  const prevLayoutRef = useRef<{ spacing: 'equal' | 'cents'; lowTop: boolean }>({
    spacing: stringSpacing,
    lowTop: stringOrderLowTop,
  });
  useEffect(() => {
    // Free Play keeps whatever layout the player is already using —
    // its whole point is "your instrument, exactly as it sits". Only
    // the tuner reshapes the strings into its cents ruler.
    if (mode !== 'tuner') return;
    prevLayoutRef.current = {
      spacing: stringSpacing,
      lowTop: stringOrderLowTop,
    };
    if (stringSpacing !== 'cents') setStringSpacing('cents');
    if (!stringOrderLowTop) setStringOrderLowTop(true);
    return () => {
      setStringSpacing(prevLayoutRef.current.spacing);
      setStringOrderLowTop(prevLayoutRef.current.lowTop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mic lifecycle: start on mount AND on visibility restored, stop
  // on unmount AND on visibility hidden. Without the visibility
  // branch, locking the iPad while the tuner is still open leaves
  // the MediaStream track "live" — the orange recording dot stays on
  // and the mic keeps draining battery until iOS eventually suspends
  // the page minutes later. AudioEngine has the same pattern in
  // Stage.tsx but only suspends the synth, not the tuner mic.
  useEffect(() => {
    let handle: TunerHandle | null = null;
    let cancelled = false;
    let starting = false;

    const start = async () => {
      if (handle || starting || cancelled || document.hidden) return;
      starting = true;
      try {
        const h = await startTuner(
          (p) => {
            if (cancelled) return;
            if (!p) {
              // Silence — reset the filter so the next pluck starts
              // fresh instead of lagging out of the previous note.
              pitchFilterRef.current.reset();
              setPitch(null);
              return;
            }
            // Adaptive One Euro smoothing on the raw YIN pitch: a held
            // note sits steady, a bend / attack tracks with near-zero
            // lag. Everything downstream — trail, nearest-string
            // routing, readout — reads this responsive-yet-smooth
            // value.
            const fMidi = pitchFilterRef.current.filter(p.midiFloat, p.at);
            setPitch({
              ...p,
              midiFloat: fMidi,
              freq: 440 * Math.pow(2, (fMidi - 69) / 12),
            });
          },
          () => {
            // The engine released the mic from inside (iOS audio
            // interruption / track mute). Clear our handle so a later
            // focus/visible event re-acquires the mic cleanly.
            handle = null;
            setMicReady(false);
          },
        );
        if (cancelled || document.hidden) {
          h.stop();
          return;
        }
        handle = h;
        setMicReady(true);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setMicError(msg);
      } finally {
        starting = false;
      }
    };

    const stop = () => {
      handle?.stop();
      handle = null;
      setMicReady(false);
    };

    // iOS Safari does not reliably fire `visibilitychange` on screen
    // lock — `pagehide` and window `blur` are extra stop triggers.
    const onVisibility = () => {
      if (document.hidden) stop();
      else void start();
    };
    const onHide = () => stop();
    const onShow = () => {
      if (!document.hidden) void start();
    };

    void start();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onHide);
    window.addEventListener('blur', onHide);
    window.addEventListener('focus', onShow);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('blur', onHide);
      window.removeEventListener('focus', onShow);
      stop();
    };
  }, []);

  // Screen Wake Lock — iPad blanks the screen during tuning because
  // mic + canvas + RAF don't count as user activity. Hold a screen
  // wake lock while the tuner is open. iOS auto-releases on tab hide,
  // so re-acquire on visibilitychange when we come back to visible.
  useEffect(() => {
    const wl = navigator.wakeLock;
    if (!wl) return;

    let sentinel: WakeLockSentinel | null = null;
    let cancelled = false;

    const acquire = async () => {
      if (cancelled || document.hidden || sentinel) return;
      try {
        sentinel = await wl.request('screen');
        sentinel.addEventListener('release', () => {
          sentinel = null;
        });
      } catch {
        // Feature/permission denied — silently skip; the tuner still
        // works, the player just needs to tap the screen periodically.
      }
    };

    const onVisibility = () => {
      if (!document.hidden) void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void sentinel?.release().catch(() => {});
      sentinel = null;
    };
  }, []);

  // --- Find the nearest string (target) and cent offset for the
  //     LIVE pitch. Used to route trail samples to the right string
  //     so the trail itself stays responsive to bend / vibrato motion.
  const nearestString = useMemo(() => {
    if (!pitch) return null;
    const midiFloat = pitch.midiFloat;
    let bestIdx = -1;
    let bestDelta = Infinity;
    for (let i = 0; i < derived.length; i++) {
      const s = derived[i]!;
      const stringMidi = s.midi + s.cents / 100;
      const d = Math.abs(stringMidi - midiFloat);
      if (d < bestDelta) {
        bestDelta = d;
        bestIdx = i;
      }
    }
    if (bestIdx < 0) return null;
    const s = derived[bestIdx]!;
    const stringMidi = s.midi + s.cents / 100;
    const centsOff = Math.round((pitch.midiFloat - stringMidi) * 100);
    return { stringIndex: s.stringIndex, note: s, centsOff };
  }, [pitch, derived]);

  // --- Same nearest-string mapping, but anchored to the STABLE pitch
  //     so the readout chip below stays parked on the longest-held
  //     note even while vibrato moves the live pitch.
  const stableNearest = useMemo(() => {
    if (!stablePitch) return null;
    const midiFloat = stablePitch.midiFloat;
    let bestIdx = -1;
    let bestDelta = Infinity;
    for (let i = 0; i < derived.length; i++) {
      const s = derived[i]!;
      const stringMidi = s.midi + s.cents / 100;
      const d = Math.abs(stringMidi - midiFloat);
      if (d < bestDelta) {
        bestDelta = d;
        bestIdx = i;
      }
    }
    if (bestIdx < 0) return null;
    const s = derived[bestIdx]!;
    const stringMidi = s.midi + s.cents / 100;
    const centsOff = Math.round((midiFloat - stringMidi) * 100);
    return { stringIndex: s.stringIndex, note: s, centsOff };
  }, [stablePitch, derived]);

  // Capture each detection into BOTH trail structures:
  //  · trailRef   — the single continuous main line (absolute pitch).
  //  · trailsRef  — per-string rows, used only by the tuner strip.
  useEffect(() => {
    if (!pitch || !nearestString) return;

    const main = trailRef.current;
    const prevM = main[main.length - 1];

    // Transient octave / harmonic-error rejection. A sample that leaps
    // far from the established pitch and then snaps back is a YIN
    // mis-detection — DROP it entirely so the line bridges across
    // smoothly instead of breaking + leaving a hole. A SUSTAINED leap
    // (the player genuinely changed note) survives: after a few
    // dropped frames the streak cap lets it through, where JUMP_BREAK
    // then starts a clean new run.
    if (prevM !== undefined) {
      const jumpSemis = Math.abs(pitch.midiFloat - prevM.midiFloat);
      if (jumpSemis > JUMP_REJECT_SEMIS && jumpDropStreakRef.current < 3) {
        jumpDropStreakRef.current += 1;
        return;
      }
    }
    jumpDropStreakRef.current = 0;

    const inTune = Math.abs(nearestString.centsOff) <= IN_TUNE_CENTS;

    // --- Main continuous line ---
    const continuousM =
      prevM !== undefined &&
      pitch.at - prevM.t < GAP_BREAK_MS &&
      Math.abs(pitch.midiFloat - prevM.midiFloat) * 100 < JUMP_BREAK_CENTS;
    main.push({
      t: pitch.at,
      midiFloat: pitch.midiFloat,
      rms: pitch.rms,
      inTune,
      continuous: continuousM,
    });
    if (main.length > 600) main.shift();

    // --- Per-string trail (tuner strip only) ---
    const trails = trailsRef.current;
    let trail = trails.get(nearestString.stringIndex);
    if (!trail) {
      trail = [];
      trails.set(nearestString.stringIndex, trail);
    }
    const prev = trail[trail.length - 1];
    const continuous =
      prev !== undefined &&
      pitch.at - prev.t < GAP_BREAK_MS &&
      Math.abs(nearestString.centsOff - prev.centsOff) < JUMP_BREAK_CENTS;
    trail.push({
      t: pitch.at,
      centsOff: nearestString.centsOff,
      inTune,
      rms: pitch.rms,
      continuous,
    });
    if (trail.length > 300) trail.shift();
  }, [pitch, nearestString]);

  // Canvas sizing — its own one-time effect, independent of the RAF
  // loop. Resize observer fires whenever the canvas's CSS box changes.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  // Live tuning ref so the draw loop reads the freshest layout each
  // frame without re-subscribing the RAF when the user re-tunes.
  const derivedRef = useRef(derived);
  derivedRef.current = derived;

  // Live string order for the draw loop. The tuner's layout effect
  // forces this to low-on-top; Free Play leaves it at the player's
  // current setting so the trail rides the strings exactly as
  // StringsOverlay paints them.
  const orderLowTopRef = useRef(stringOrderLowTop);
  orderLowTopRef.current = stringOrderLowTop;

  // Canvas drawing loop — runs every frame regardless of pitch so the
  // trail fades continuously (even when the mic goes quiet).
  useRafLoop(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const liveDerived = derivedRef.current;
    // Recompute string Ys each frame (cheap) — lets the trail follow
    // layout changes (spacing toggle, string count) without a re-render.
    const pitches = liveDerived.map((d) => d.midi + d.cents / 100);
    const stringYs = computeStringYs(pitches.length, {
      spacing: 'cents',
      orderLowTop: orderLowTopRef.current,
      pitches,
    });
    // Row-half in normalised units per string: half-distance to the
    // nearest neighbour, clamped to a minimum so very dense clusters
    // don't collapse the trail to a hairline.
    const rowHalf: number[] = stringYs.map((_, i) => {
      const prev = i > 0 ? Math.abs(stringYs[i]! - stringYs[i - 1]!) : Infinity;
      const next =
        i < stringYs.length - 1
          ? Math.abs(stringYs[i + 1]! - stringYs[i]!)
          : Infinity;
      const neighbour = Math.min(prev, next);
      return Math.max(0.012, neighbour * 0.5 * ROW_FILL);
    });

    const now = performance.now();
    const trails = trailsRef.current;
    const cutoff = now - TRAIL_SECONDS * 1000;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const W = canvas.width;
    const H = canvas.height;
    const dpr = window.devicePixelRatio || 1;

    // The 10× precision strip is the TUNER's signature — its presence
    // is what made Free Play indistinguishable from the tuner. Free
    // Play drops it entirely: the trail uses the full canvas width
    // (drawW = W). Everything ELSE about the trail — cent mapping,
    // smoothing, stroke — is rendered identically to the tuner. The
    // tuner keeps the strip; it floors at 160 CSS px so it stays
    // readable on narrow phones.
    const stripWidth =
      mode === 'tuner'
        ? Math.max(Math.round(W * STRIP_WIDTH_FRAC), Math.round(160 * dpr))
        : 0;
    const drawW = W - stripWidth;
    const stripEndX = W;

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Age-out the per-string trails (used only by the tuner strip).
    for (const t of trails.values()) {
      while (t.length > 0 && t[0]!.t < cutoff) t.shift();
    }

    // Active string = the string nearest the most recent detected
    // pitch. Drives the tuner's precision strip + readout-chip
    // placement only; the main line itself uses no string at all.
    let activeStringIndex = -1;
    {
      const mt0 = trailRef.current;
      const last = mt0[mt0.length - 1];
      if (last) {
        let best = Infinity;
        for (let i = 0; i < pitches.length; i++) {
          const d = Math.abs(pitches[i]! - last.midiFloat);
          if (d < best) {
            best = d;
            activeStringIndex = i;
          }
        }
      }
    }
    if (activeStringIndex >= 0) lastActiveStringRef.current = activeStringIndex;
    else activeStringIndex = lastActiveStringRef.current;

    // Stroke one continuous run of the main line as a single smooth
    // polyline — quadratic-smoothed, with a tail→head alpha gradient.
    // Stroking per-segment re-painted the round line-cap at every
    // joint and beaded the line; one path per run is genuinely smooth.
    const strokeRun = (run: DrawPoint[], width: number, layerAlpha: number) => {
      if (run.length < 2) return;
      const x0 = run[0]!.x;
      const span = Math.max(1, run[run.length - 1]!.x - x0);
      const grad = ctx.createLinearGradient(x0, 0, x0 + span, 0);
      let lastOff = -1;
      for (const p of run) {
        let off = Math.min(1, Math.max(0, (p.x - x0) / span));
        // addColorStop needs non-decreasing offsets — nudge past dups.
        if (off <= lastOff) off = Math.min(1, lastOff + 1e-4);
        lastOff = off;
        const rgb = p.inTune ? TRAIL_RGB_INTUNE : TRAIL_RGB;
        grad.addColorStop(off, `rgba(${rgb}, ${p.alpha * layerAlpha})`);
      }
      ctx.strokeStyle = grad;
      ctx.lineWidth = width * dpr;
      ctx.beginPath();
      ctx.moveTo(run[0]!.x, run[0]!.y);
      if (run.length === 2) {
        ctx.lineTo(run[1]!.x, run[1]!.y);
      } else {
        // Quadratic smoothing — curve through the midpoints between
        // consecutive samples so the 60 Hz polyline reads as a smooth
        // wave, not a zig-zag.
        for (let i = 1; i < run.length - 1; i++) {
          const mx = (run[i]!.x + run[i + 1]!.x) / 2;
          const my = (run[i]!.y + run[i + 1]!.y) / 2;
          ctx.quadraticCurveTo(run[i]!.x, run[i]!.y, mx, my);
        }
        const last = run[run.length - 1]!;
        ctx.quadraticCurveTo(
          run[run.length - 2]!.x,
          run[run.length - 2]!.y,
          last.x,
          last.y,
        );
      }
      ctx.stroke();
    };

    // ===== Main pitch line =====
    // ONE continuous line in absolute-pitch space. The pitch is mapped
    // straight to Y (interpolated through the cents-spaced string
    // layout), so the line flows freely between string rows. The old
    // per-string routing chopped the pitch onto 17 separate rows — a
    // pitch sitting near a string boundary flickered between rows and
    // drew as a doubled, fragmented mess. A single line cannot do that
    // — exactly how a one-string pitch display (Air Đàn Bầu) stays
    // smooth.
    const mainTrail = trailRef.current;
    while (mainTrail.length > 0 && mainTrail[0]!.t < cutoff) mainTrail.shift();

    if (mainTrail.length >= 2) {
      // Absolute pitch (semitones) → normalised Y. Piecewise-linear
      // through the string layout; extrapolates past the end strings.
      const layout = pitches
        .map((p, i) => ({ p, y: stringYs[i]! }))
        .sort((a, b) => a.p - b.p);
      const pitchToY = (P: number): number => {
        const n = layout.length;
        if (n === 0) return 0.5;
        if (n === 1) return layout[0]!.y;
        let lo = 0;
        if (P >= layout[n - 1]!.p) {
          lo = n - 2;
        } else if (P > layout[0]!.p) {
          for (let i = 0; i < n - 1; i++) {
            if (P <= layout[i + 1]!.p) {
              lo = i;
              break;
            }
          }
        }
        const a = layout[lo]!;
        const b = layout[lo + 1]!;
        const denom = b.p - a.p;
        if (denom === 0) return a.y;
        return a.y + ((P - a.p) / denom) * (b.y - a.y);
      };

      // dB-loudness × quadratic age fade — a quiet / decaying note
      // tapers away smoothly with nothing marking its end.
      const pts: DrawPoint[] = mainTrail.map((p) => {
        const age = (now - p.t) / (TRAIL_SECONDS * 1000);
        const ageAlpha = Math.max(0, 1 - age * age);
        return {
          x: drawW * (1 - age),
          y: pitchToY(p.midiFloat) * H,
          alpha: ageAlpha * rmsToAlpha(p.rms),
          inTune: p.inTune,
          continuous: p.continuous,
        };
      });

      // Partition into continuous runs; stroke each as glow + core.
      // Transient errors are already dropped at capture, so only truly
      // isolated 1-point runs need skipping here.
      const MIN_RUN = 2;
      let runStart = 0;
      for (let i = 1; i <= pts.length; i++) {
        if (i === pts.length || !pts[i]!.continuous) {
          const run = pts.slice(runStart, i);
          if (run.length >= MIN_RUN) {
            strokeRun(run, 9, 0.3);  // soft outer glow
            strokeRun(run, 5, 1.0);  // crisp core
          }
          runStart = i;
        }
      }

      // Live cents readout at the trail head — colour-tiered in-tune
      // green / close yellow / far red.
      const tip = pts[pts.length - 1]!;
      if (nearestString && tip.alpha > 0.2) {
        const liveCents = nearestString.centsOff;
        const absC = Math.abs(liveCents);
        const textRgb =
          absC <= IN_TUNE_CENTS
            ? '134, 239, 172'
            : absC <= CLOSE_CENTS
              ? '253, 224, 71'
              : '252, 165, 165';
        const label = `${liveCents > 0 ? '+' : ''}${liveCents}¢`;
        ctx.save();
        ctx.font = `700 ${30 * dpr}px ui-monospace, "SF Mono", Menlo, monospace`;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'right';
        ctx.shadowBlur = 8 * dpr;
        ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        ctx.fillStyle = `rgba(${textRgb}, ${Math.min(1, tip.alpha + 0.3)})`;
        ctx.fillText(label, tip.x - 22 * dpr, tip.y);
        ctx.restore();
      }
    }

    // ===== Right-edge precision strip =====
    // A thin column where the active string's row is replayed at 10×
    // vertical magnification with LINEAR cent spacing. Tick spacing is
    // even (each cent = same pixel distance) and well outside the main
    // display, so the player can resolve ±20¢ → 0¢ at high precision
    // without crowding the rest of the layout. Tuner-only — Free Play
    // has no strip.
    if (mode === 'tuner' && activeStringIndex >= 0) {
      const sy = stringYs[activeStringIndex];
      const rh = rowHalf[activeStringIndex];
      const trail = trails.get(activeStringIndex);
      if (
        sy !== undefined &&
        rh !== undefined &&
        trail &&
        trail.length >= 1
      ) {
        const rowHalfPx = rh * H;
        const rowCenterPx = sy * H;
        const stripX = drawW;

        // Backdrop + separator. FULLY opaque so the StringsOverlay
        // beneath (other strings drawn at full canvas width) doesn't
        // bleed through into the strip — only the strip's own active
        // string line should be visible here.
        ctx.fillStyle = '#0a121a';
        ctx.fillRect(stripX, 0, stripWidth, H);
        ctx.strokeStyle = `rgba(${TRAIL_RGB}, 0.55)`;
        ctx.lineWidth = 1.5 * dpr;
        ctx.beginPath();
        ctx.moveTo(stripX + 0.5, 0);
        ctx.lineTo(stripX + 0.5, H);
        ctx.stroke();

        // Linear cents → y inside strip. The main row's rowHalfPx spans
        // ±MAX_VIEW_CENTS via sqrt; we multiply by STRIP_MAGNIFICATION
        // and use linear so ticks land at evenly spaced y positions.
        // Far misses overflow off the visible strip (the trail simply
        // exits — its real coordinate isn't useful for fine tuning).
        const stripMapY = (cents: number) => {
          const c = Math.max(
            -MAX_VIEW_CENTS,
            Math.min(MAX_VIEW_CENTS, cents),
          );
          return (
            rowCenterPx +
            (c / MAX_VIEW_CENTS) * rowHalfPx * STRIP_MAGNIFICATION
          );
        };

        // Active string line continued into the strip
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.lineWidth = 1.8 * dpr;
        ctx.beginPath();
        ctx.moveTo(stripX + 4 * dpr, rowCenterPx);
        ctx.lineTo(stripEndX - 4 * dpr, rowCenterPx);
        ctx.stroke();

        // Tick marks + labels (only those that fit inside the strip)
        for (const c of [-20, -10, -5, 5, 10, 20]) {
          const y = stripMapY(c);
          if (y < 4 * dpr || y > H - 4 * dpr) continue;
          const major = Math.abs(c) === 5 || Math.abs(c) === 20;
          ctx.strokeStyle = `rgba(255, 255, 255, ${major ? 0.45 : 0.22})`;
          ctx.lineWidth = 1 * dpr;
          ctx.beginPath();
          ctx.moveTo(stripX + 4 * dpr, y);
          ctx.lineTo(stripEndX - 4 * dpr, y);
          ctx.stroke();
          ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
          ctx.font = `${10 * dpr}px ui-monospace, Menlo, monospace`;
          ctx.fillText(`${c > 0 ? '+' : ''}${c}`, stripX + 6 * dpr, y - 3 * dpr);
        }

        // Trail inside strip — x compressed into stripWidth, y linear.
        if (trail.length >= 2) {
          ctx.save();
          ctx.beginPath();
          ctx.rect(stripX + 1 * dpr, 0, stripWidth - 1 * dpr, H);
          ctx.clip();
          for (let i = 1; i < trail.length; i++) {
            const a = trail[i - 1]!;
            const b = trail[i]!;
            if (!b.continuous) continue;
            const ageA = (now - a.t) / (TRAIL_SECONDS * 1000);
            const ageB = (now - b.t) / (TRAIL_SECONDS * 1000);
            if (ageA > 1) continue;
            const xA = stripX + stripWidth * (1 - ageA);
            const xB = stripX + stripWidth * (1 - ageB);
            const yA = stripMapY(a.centsOff);
            const yB = stripMapY(b.centsOff);
            const alphaA =
              Math.max(0, 1 - ageA * ageA) * rmsToAlpha(a.rms);
            const alphaB =
              Math.max(0, 1 - ageB * ageB) * rmsToAlpha(b.rms);
            const alpha = Math.min(alphaA, alphaB);
            if (alpha <= 0) continue;
            const isInTune = a.inTune && b.inTune;
            const rgb = isInTune ? TRAIL_RGB_INTUNE : TRAIL_RGB;
            ctx.strokeStyle = `rgba(${rgb}, ${0.5 * alpha})`;
            ctx.lineWidth = 5 * dpr;
            ctx.beginPath();
            ctx.moveTo(xA, yA);
            ctx.lineTo(xB, yB);
            ctx.stroke();
            ctx.strokeStyle = `rgba(${rgb}, ${alpha})`;
            ctx.lineWidth = (isInTune ? 2.6 : 2) * dpr;
            ctx.beginPath();
            ctx.moveTo(xA, yA);
            ctx.lineTo(xB, yB);
            ctx.stroke();
          }
          // No head dot here either — the strip trail, like the main
          // trail, just fades out by per-point alpha.
          ctx.restore();
        }
      }
    }

    // Readout-chip placement. Locked per sub-stroke: when a new pluck
    // (or string switch) starts, the chip jumps to a spot that dodges
    // the trail tip. During the sustained sound the chip stays put —
    // no following vibrato or decay drift.
    // Chip placement is strip-relative — it parks the readout in the
    // strip column, dodging the trail tip. Tuner-only; Free Play has
    // no strip, so its readout chip stays parked top-centre via CSS.
    if (mode === 'tuner' && readoutRef.current) {
      const stripCenterCss = (drawW + stripWidth / 2) / dpr;
      const cssH = H / dpr;
      const margin = 18;
      // Use the chip's real rendered height so placement adapts when
      // the fluid font sizes (clamp on mobile / iPad) shrink the chip.
      const chipHeightCss = readoutRef.current.offsetHeight || 140;

      // Find the active string's most-recent NEW-stroke anchor:
      // scan back to the most-recent point with continuous=false.
      let anchor: {
        stringIdx: number;
        t: number;
        centsOff: number;
        rowCenterPx: number;
        rowHalfPx: number;
      } | null = null;
      if (activeStringIndex >= 0) {
        const sy = stringYs[activeStringIndex];
        const rh = rowHalf[activeStringIndex];
        const trail = trails.get(activeStringIndex);
        if (sy !== undefined && rh !== undefined && trail && trail.length > 0) {
          let anchorPt = trail[0]!;
          for (let i = trail.length - 1; i >= 0; i--) {
            if (!trail[i]!.continuous) {
              anchorPt = trail[i]!;
              break;
            }
          }
          anchor = {
            stringIdx: activeStringIndex,
            t: anchorPt.t,
            centsOff: anchorPt.centsOff,
            rowCenterPx: sy * H,
            rowHalfPx: rh * H,
          };
        }
      }

      const prevAnchor = chipAnchorRef.current;
      const isNewStroke =
        anchor !== null &&
        (prevAnchor === null ||
          prevAnchor.stringIdx !== anchor.stringIdx ||
          prevAnchor.t !== anchor.t);

      if (isNewStroke && anchor) {
        const clampedCents = Math.max(
          -MAX_VIEW_CENTS,
          Math.min(MAX_VIEW_CENTS, anchor.centsOff),
        );
        const tipCanvasY =
          anchor.rowCenterPx +
          (clampedCents / MAX_VIEW_CENTS) *
            anchor.rowHalfPx *
            STRIP_MAGNIFICATION;
        const tipCssY = tipCanvasY / dpr;
        // Three discrete slots: highest, middle, lowest. On a new
        // pluck, snap to whichever slot's CENTER sits farthest from
        // the pitch tip. Result: the chip is always at the maximum
        // visual distance from the pitch line that the layout allows.
        const slotTops = [
          margin,
          (cssH - chipHeightCss) / 2,
          cssH - chipHeightCss - margin,
        ];
        let chipTopCss = slotTops[0]!;
        let bestDist = -1;
        for (const top of slotTops) {
          const slotCenter = top + chipHeightCss / 2;
          const d = Math.abs(slotCenter - tipCssY);
          if (d > bestDist) {
            bestDist = d;
            chipTopCss = top;
          }
        }
        chipPosRef.current = { leftCss: stripCenterCss, topCss: chipTopCss };
        chipAnchorRef.current = { stringIdx: anchor.stringIdx, t: anchor.t };
      } else if (!chipPosRef.current) {
        // First frame, no pluck yet — park near the top of the strip.
        chipPosRef.current = { leftCss: stripCenterCss, topCss: margin };
      } else {
        // Keep horizontal centre in sync on resize, but leave vertical
        // alone so the chip stays stationary mid-sound.
        chipPosRef.current.leftCss = stripCenterCss;
      }

      readoutRef.current.style.left = `${chipPosRef.current.leftCss}px`;
      readoutRef.current.style.top = `${chipPosRef.current.topCss}px`;
    }
  });

  const note = stablePitch ? freqToNote(stablePitch.freq) : null;

  return (
    <div
      className="tuner"
      role="dialog"
      aria-label={mode === 'tuner' ? 'Tuner' : 'Free play'}
    >
      {/* Exit is framed as "back to tab view" — DanTranhTab has no
          AirDanTranh camera mode, so the AIR label that the AirDanTranh
          fork used here would be misleading. The two views (tab + tuner)
          are equal peers, not modal parent/child. */}
      <button
        type="button"
        className="tuner__air"
        onClick={onClose}
        aria-label="Back to tab"
      >
        TAB
      </button>

      <canvas className="tuner__trail" ref={canvasRef} />

      <div
        className={
          'tuner__readout' +
          // Free Play never renders a green/right or red/wrong verdict
          // — bending OFF a string is the technique being practised,
          // not an error. The chip stays neutral.
          (mode === 'tuner' && stableNearest
            ? Math.abs(stableNearest.centsOff) <= 10
              ? ' tuner__readout--intune'
              : ' tuner__readout--off'
            : '')
        }
        ref={readoutRef}
      >
        {micError ? (
          <div className="tuner__error">Mic error — {micError}</div>
        ) : !micReady ? (
          <div className="tuner__hint">Waking up mic…</div>
        ) : stableNearest && stablePitch && note ? (
          <>
            <div
              className="tuner__note"
              style={{
                // Note label tinted to match the string's anchor colour
                // (yellow root / red fifth / white otherwise) — same
                // palette StringsOverlay uses for the string lines, so
                // the chip reads as belonging to that string. The
                // tuned/off glow rides on top via text-shadow from the
                // tuner__readout--intune / --off modifiers.
                color: stableNearest.note.isRoot
                  ? '#ffd24a'
                  : stableNearest.note.isPerfectFifth
                    ? '#ef4444'
                    : '#ffffff',
              }}
            >
              {note.pitchClass}
              <span className="tuner__octave">{note.octave}</span>
              <span
                className={
                  'tuner__cents' +
                  (Math.abs(stableNearest.centsOff) <= IN_TUNE_CENTS
                    ? ' tuner__cents--intune'
                    : stableNearest.centsOff > 0
                      ? ' tuner__cents--sharp'
                      : ' tuner__cents--flat')
                }
              >
                {stableNearest.centsOff > 0 ? '+' : ''}
                {stableNearest.centsOff}¢
              </span>
            </div>
            <div className="tuner__target">
              string {stableNearest.stringIndex + 1}
              {' · '}
              {mode === 'tuner' ? 'target ' : 'nearest '}
              {stableNearest.note.pitchClass}
              {stableNearest.note.octave}
              {' · '}
              {stableNearest.note.freq.toFixed(1)} Hz
            </div>
          </>
        ) : (
          <div className="tuner__hint">
            {mode === 'tuner'
              ? 'Pluck a string…'
              : 'Pluck, bend or vibrato — watch your pitch line'}
          </div>
        )}
      </div>
    </div>
  );
}
